import {
  Children,
  cloneElement,
  isValidElement,
  useId,
  type HTMLAttributes,
  type PropsWithChildren,
} from 'react'

interface FormFieldProps extends PropsWithChildren {
  label: string
  htmlFor: string
  hint?: string
  error?: string
  required?: boolean
}

export function FormField({
  label,
  htmlFor,
  hint,
  error,
  required = false,
  children,
}: FormFieldProps) {
  const descriptionId = useId()
  const hintId = `${descriptionId}-hint`
  const errorId = `${descriptionId}-error`
  const fieldChildren = Children.map(children, (child) => {
    if (
      !isValidElement<HTMLAttributes<HTMLElement>>(child) ||
      typeof child.type !== 'string' ||
      !['input', 'select', 'textarea'].includes(child.type)
    ) {
      return child
    }
    const describedBy = [
      child.props['aria-describedby'],
      error ? errorId : hint ? hintId : undefined,
    ]
      .filter(Boolean)
      .join(' ')

    return cloneElement(child, {
      ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      ...(error ? { 'aria-invalid': true } : {}),
      ...(required ? { 'aria-required': true } : {}),
    })
  })

  return (
    <div className={`form-field${error ? ' form-field--error' : ''}`}>
      <label htmlFor={htmlFor}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      {fieldChildren}
      {hint && !error ? (
        <span className="form-field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="form-field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  )
}
